import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Column, SortState } from './data-table-model';
import { cn } from '../lib/cn';

export interface DataTableProps<Row> {
  caption: string;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string | number;
  dense?: boolean;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  onRowClick?: (row: Row) => void;
  /** Keyboard selection highlight. */
  selectedKey?: string | number | null;
  className?: string;
}

/** Data table (CLAUDE.md §6.8): sticky header, tabular right-aligned numbers, aria-sort. */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  dense = false,
  sort = null,
  onSortChange,
  onRowClick,
  selectedKey = null,
  className,
}: DataTableProps<Row>) {
  return (
    <table className={cn('w-full border-collapse', dense ? 'text-xs' : 'text-sm', className)}>
      <caption className="sr-only">{caption}</caption>
      <thead className="sticky top-0 bg-bg-1">
        <tr>
          {columns.map((column) => {
            const active = sort?.column === column.id;
            const ariaSort = active
              ? sort.direction === 'asc'
                ? 'ascending'
                : 'descending'
              : undefined;
            const label =
              column.sortValue && onSortChange ? (
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center gap-1 rounded-sm outline-none hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus',
                    column.numeric === true && 'flex-row-reverse',
                  )}
                  onClick={() => {
                    onSortChange({
                      column: column.id,
                      direction: active && sort.direction === 'desc' ? 'asc' : 'desc',
                    });
                  }}
                >
                  {column.header}
                  {active &&
                    (sort.direction === 'asc' ? (
                      <ChevronUp className="size-3.5" aria-hidden />
                    ) : (
                      <ChevronDown className="size-3.5" aria-hidden />
                    ))}
                </button>
              ) : (
                column.header
              );
            return (
              <th
                key={column.id}
                scope="col"
                aria-sort={ariaSort}
                className={cn(
                  'border-b border-border-subtle px-2 py-1.5 text-xs font-medium whitespace-nowrap text-text-3',
                  column.numeric === true ? 'text-right' : 'text-left',
                )}
              >
                {label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const key = rowKey(row);
          return (
            <tr
              key={key}
              aria-selected={onRowClick ? key === selectedKey : undefined}
              className={cn(
                'hover:bg-bg-3',
                onRowClick && 'cursor-pointer focus-visible:bg-bg-3',
                key === selectedKey && 'bg-bg-4 hover:bg-bg-4',
              )}
              // Clickable rows are reachable and openable from the keyboard too.
              tabIndex={onRowClick ? 0 : undefined}
              onClick={
                onRowClick
                  ? () => {
                      onRowClick(row);
                    }
                  : undefined
              }
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.id}
                  className={cn(
                    'px-2 py-1 whitespace-nowrap',
                    column.numeric === true && 'tabular text-right',
                  )}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
